import { Coordinates } from '@shared/interfaces/project/coordinates.interface';
import { TableResponse } from '@shared/models/table-response.model';
import { Participant } from '@shared/interfaces/event/participant.interface';
import { StorageService } from '@shared/services/storage.service';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BaseHttpService } from '@shared/services/base-http.service';
import { Tag } from '@shared/interfaces/project/tag.interface';
import { Types } from '@shared/interfaces/event/types.interface';
import { Project } from '@shared/models/project.model';
import { Event } from '@shared/models/event.model';
import { UploadImg } from '@shared/interfaces/project/upload-img.interface';
import { Response } from '@shared/models/response.model';
import { SuccessRes } from '@shared/interfaces/success-res.interface';
import { Organisation } from '@shared/interfaces/event/organisation.interface';
import { Theme } from '@shared/interfaces/event/theme.interface';
import * as moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { AppInjector } from '@shared/services/app-injector.service';

@Injectable({
  providedIn: 'root',
})
export class EventsService {
  translate: TranslateService;
  storage: StorageService;

  constructor(public baseHttp: BaseHttpService) {
    const injector = AppInjector.getInjector();
    this.translate = injector.get(TranslateService);
    this.storage = injector.get(StorageService);
  }

  tagList(): Observable<Response<Tag[]>> {
    return this.baseHttp.get('/tag').pipe(map((resp) => resp as Response<Tag[]>));
  }

  organisationsList(): Observable<Response<Organisation[]>> {
    return this.baseHttp.get('/organization').pipe(map((resp) => resp as Response<Organisation[]>));
  }

  themeList(): Observable<Response<Theme[]>> {
    return this.baseHttp.get('/event-theme').pipe(map((resp) => resp as Response<Theme[]>));
  }

  typeList(): Observable<Response<Types[]>> {
    return this.baseHttp.get('/event/types').pipe(map((resp) => resp as Response<Types[]>));
  }

  getCoordinates(): Observable<Response<Coordinates>> {
    return this.baseHttp
      .get('/parameters/get-ip-coords')
      .pipe(map((resp) => resp as Response<Coordinates>));
  }

  newEvent(pack: Event): Observable<Response<Event>> {
    return this.baseHttp
      .post('/event', { event: pack })
      .pipe(map((resp) => resp as Response<Event>));
  }

  editEvent(pack: Event, id: number): Observable<Response<Event>> {
    return this.baseHttp
      .put('/event/' + id, { event: pack })
      .pipe(map((resp) => resp as Response<Event>));
  }

  deleteDraftEvent(id: number) {
    return this.baseHttp.delete('/event/delete-draft/' + id);
  }

  convertToProject(obj) {
    return this.baseHttp.put('/event/create-project', obj);
  }

  setImage(formData) {
    return this.baseHttp.post('/event/upload/image', formData).pipe(map((resp) => resp));
  }

  updateImgData(pack: UploadImg, id: number): Observable<Response<Project>> {
    return this.baseHttp
      .post('/files/' + id, { file: { meta_data: pack.data } })
      .pipe(map((resp) => resp as Response<Project>));
  }

  uploadImg(pack: UploadImg): Observable<SuccessRes> {
    return this.baseHttp
      .postQueue('/files/project/' + pack.id + '/' + pack.container, pack.imgData)
      .pipe(map((resp) => resp as SuccessRes));
  }

  getMonth(dateSEntry) {
    let date = moment(dateSEntry, 'YYYY/MM/DD', this.storage.getLang());
    return date.format('MMMM');
  }

  getDay(dateSEntry) {
    let date = moment(dateSEntry, 'YYYY/MM/DD');

    return date.format('D');
  }

  getYear(dateSEntry) {
    let date = moment(dateSEntry, 'YYYY/MM/DD');
    return date.format('YYYY');
  }

  getTime(date) {
    let time = moment(date, 'YYYY-MM-DD HH:mm');
    return time.format('HH:mm');
  }

  createReplayToComment(pack): Observable<Response<Comment>> {
    return this.baseHttp
      .post('/event/comment/reply', { event_comment_reply: pack })
      .pipe(map((resp) => resp as Response<Comment>));
  }

  getParticipantsList(
    id: number,
    page: number = 0
  ): Observable<Response<TableResponse<Participant[]>>> {
    return this.baseHttp
      .get('/event/participant/' + id + '?page=' + page)
      .pipe(map((resp) => resp as Response<TableResponse<Participant[]>>));
  }
}
